import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ACTION_READ_ANY_GRANTS } from '../../../auth/grants'
import { useCurrentUser, useGrants } from '../../../auth/useCurrentUser'
import { setActionFollowed } from '../api/actionApi'
import { ACTION_TAB_SCOPES, type ActionListItem, type ActionScope } from '../types'
import { useProxyMode } from '../../proxy/useProxyMode'

/**
 * Yıldız bu kullanıcıya çizilsin mi (120)? İki koşul birden:
 *
 * - **Sicil** — takip sicil üzerinden tutulur; sicili olmayan kullanıcıda uç 400 döner, düğme
 *   çizilmez (spec Edge Cases).
 * - **Görev okuma yetkisi** — uç `HAS_ACTION_READ_ANY` ile korunur; aynası {@link ACTION_READ_ANY_GRANTS}.
 *   Görev listelerinde bu zaten sağlanmıştır; asıl anlamı **Toplantı Detay**'dadır: tutanağı görebilen
 *   ama görev ekranlarına hiç yetkisi olmayan bir kullanıcıya 403 dönecek bir düğme gösterilmez.
 *
 * Satır düzeyindeki kural ("yalnız kaydedilmiş ana görev") çağıranın işidir — bu hook kullanıcıya bakar.
 */
export function useCanFollowActions(): boolean {
  const { data: me } = useCurrentUser()
  const { hasAnyGrant } = useGrants()
  // Kısıtlı Yetkili asistan kipinde SUNULMAZ (126 FR-019). Yetki kümesi burada hiçbir şey yapamaz:
  // takip uçları (`PUT`/`DELETE /{id}/follow`) `HAS_ACTION_READ_ANY` ile korunuyor, yani OKUMA
  // kodlarıyla açılan yazma uçları — kodları izin listesinden çıkarmak görev okumasını da kapatırdı.
  const { readOnly } = useProxyMode()
  return (
    !readOnly && Boolean(me?.employeeId?.trim()) && hasAnyGrant(...ACTION_READ_ANY_GRANTS)
  )
}

/**
 * Görev listesinde yıldız kolonu açık mı? Kapsam: sol menüdeki **dört görev sekmesi**
 * ({@link ACTION_TAB_SCOPES} — Tüm Görevler · Oluşturduklarım · Bana Atananlar · Takip Ettiklerim; kullanıcı
 * isteği 2026-10-07). **Arşiv** (sonlanmış görevler) ve **Delegasyon** (Anasayfa kartının hedefi, sekme
 * değil) dışarıdadır. Kopya dizi yazılmaz: yeni bir sekme eklendiğinde yıldız da kendiliğinden gelir.
 */
export function useListFollowEnabled(scope: ActionScope, archived: boolean): boolean {
  const canFollow = useCanFollowActions()
  return canFollow && !archived && ACTION_TAB_SCOPES.includes(scope)
}

/**
 * Görev listesinde satır eylem kolonu (yıldız + Sil) gerekiyor mu? Panelin gövdesinde değil burada:
 * {@code ActionsListPanel}'in bilişsel karmaşıklığı Sonar eşiğinde (S3776, 15) ve her `||` bir puandır.
 */
export function hasListRowActions(
  onDelete: ((row: ActionListItem) => void) | undefined,
  followEnabled: boolean,
): boolean {
  return onDelete != null || followEnabled
}

/**
 * Görevi kendi adına takibe al / takibi bırak (120). Başarıda yıldızı taşıyan **bütün** yüzeyler
 * tazelenir: görev listeleri (Takip Ettiklerim'de satır düşer/girer), Toplantı Detay'ın görev bölümü
 * ve — önbellekteyse — Görev Detay'ın salt-okuma Takip Edenler listesi.
 */
export function useToggleActionFollow() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, followed }: { id: number; followed: boolean }) => setActionFollowed(id, followed),
    onSuccess: (_state, { id }) => {
      qc.invalidateQueries({ queryKey: ['actions'] })
      qc.invalidateQueries({ queryKey: ['meeting-detail'] })
      qc.invalidateQueries({ queryKey: ['action-detail', id] })
    },
  })
}
